import { Box, Flex, Loader, Center } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import {
  markOrphanParents,
  normalizeInlineSuggestions,
} from 'src/common/components/markdown-editor/utils';
import { Source } from 'src/common/components/markdown-editor/types';
import { EditorHelpers } from 'src/common/components/markdown-editor/plugins/EditorReadyPlugin';
import { GroupedSuggestions } from 'src/common/components/markdown-editor/plugins/SuggestionTrackerPlugin';
import { useAppContainerContext, useContentMeta } from 'src/UI/app-container';
import {
  ChatMode,
  ChatState,
  FinalChunkData,
} from 'src/UI/app-container/chat/hooks/useChatMessages';
import { ContentPaneOverlay } from 'src/UI/app-container/ContentPaneOverlay';
import { ChatActions, SiestaMode } from 'src/UI/app-container/types';
import { useAppContainer } from 'src/UI/app-container/useAppContainer';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { useCreateDocumentVersion } from '../hooks/useCreateDocumentVersion';
import { useDeleteDocumentVersions } from '../hooks/useDeleteDocumentVersions';
import { useRecordConversationDiscard } from '../hooks/useRecordConversationDiscard';
import { useDeleteDocument } from '../hooks/useDeleteDocument';
import { useDocumentVersions } from '../hooks/useDocumentVersions';
import { useRevertToVersion } from '../hooks/useRevertToVersion';
import { useSaveDocument } from '../hooks/useSaveDocument';
import { useSetPrimaryVersion } from '../hooks/useSetPrimaryVersion';
import { api } from 'src/common/utils/api';
import { DocumentPanel } from './components';
import { EditingOverlay } from './components/EditingOverlay';
import { EditPreviewSheet } from './components/EditPreviewSheet';
import SiestaActionBar from './components/SiestaActionBar';
import { prefetchSuggestedActions } from 'src/UI/app-container/sidebar/prefetchSuggestedActions';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useSubstantiateMeetingNote } from 'src/meetings/hooks/useSubstantiateMeetingNote';
import { Logger } from 'src/utils/Logger';
import { formatVersionDate } from './utils/formatVersionDate';

const EditState = {
  IDLE: 'IDLE',
  EDITING: 'EDITING',
  PREVIEW: 'PREVIEW',
  DIFF_REVIEW: 'DIFF_REVIEW',
} as const;

type EditState = (typeof EditState)[keyof typeof EditState];

type EditStateData = {
  state: EditState;
  editedContent?: string;
  originalContent?: string;
  assistantMessage?: string;
  versionId?: string | null;
  versionNumber?: number | null;
};

export type DocumentEditConfig = {
  pageType:
    | 'suitability-report-edit'
    | 'data-export-edit'
    | 'meeting-agenda-edit'
    | 'meeting-note-edit';
  title: string;
  documentTypePath: 'suitability-reports' | 'data-exports' | 'meeting-agendas' | 'meeting-notes';
  documentLabel: string;
};

type Props = {
  config: DocumentEditConfig;
};

export default function DocumentEditPage({ config }: Props) {
  const { clientId, id } = useParams<{ clientId: string; id: string }>();
  const navigate = useNavigate();
  const {
    deleteDocument,
    error: deleteDocumentError,
    reset: resetDeleteDocument,
    isLoading: isRemovingDocument,
  } = useDeleteDocument(config.documentTypePath);
  const [suggestions, setSuggestions] = useState<GroupedSuggestions>({
    default: {},
    note: {},
    inline: {},
    placeholder: {},
    deletion: {},
  });
  const [showMarkers, setShowMarkers] = useState(true);
  const [showCitations, setShowCitations] = useState(true);
  const { versions, isLoading: isLoadingVersions, refetch } = useDocumentVersions(id);
  const { createDocumentVersion } = useCreateDocumentVersion(id);
  const { deleteDocumentVersions } = useDeleteDocumentVersions(id);
  const { recordConversationDiscard } = useRecordConversationDiscard();
  const { setPrimaryVersion } = useSetPrimaryVersion(id);
  const { saveDocument } = useSaveDocument(id);
  const { revertToVersion } = useRevertToVersion(id);
  const tagCitations = async (content: string) => {
    const result = await api.post<{ taggedContent: string }>(`documents/${id}/tag-citations`, {
      content,
    });
    return result.data.taggedContent;
  };
  const { createLink } = useFileTemporaryLinkMutation();
  const { flagEnabledMeetingNoteClaims } = useFlags();
  const { substantiate } = useSubstantiateMeetingNote(clientId ?? '');
  const editorHelpersRef = useRef<EditorHelpers | null>(null);
  const previousSiestaModeRef = useRef<SiestaMode | null>(null);
  const chatActionsRef = useRef<ChatActions | null>(null);
  const actionBarRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef(suggestions);

  const handleSuggestionsChange = (newSuggestions: GroupedSuggestions) => {
    suggestionsRef.current = newSuggestions;
    setSuggestions(newSuggestions);
  };
  const { siestaMode, setSiestaMode, setSiestaBusyGlowTop } = useAppContainer();
  const { conversationId } = useAppContainerContext();
  const queryClient = useQueryClient();

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [editorInstanceKey, setEditorInstanceKey] = useState<string | null>(null);
  const preInlineEditContentRef = useRef<string | null>(null);
  const lastSavedContentRef = useRef<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [editState, setEditState] = useState<EditStateData>({
    state: EditState.IDLE,
  });
  const inlineSuggestions = Object.values(suggestions.inline || {});
  const deletionSuggestions = Object.values(suggestions.deletion || {});
  const diffSuggestions = [...inlineSuggestions, ...deletionSuggestions];
  const hasDiffSuggestions = diffSuggestions.length > 0;
  const effectiveShowMarkers = hasDiffSuggestions || showMarkers;
  const hiddenSources: Source[] = [];
  if (!showCitations) hiddenSources.push('note');
  if (!effectiveShowMarkers) hiddenSources.push('default', 'inline', 'placeholder', 'deletion');

  const primaryVersion = versions.find(v => v.isPrimary);

  const defaultVersion = (() => {
    if (primaryVersion) return primaryVersion;
    if (versions.length === 0) return undefined;
    return versions.reduce((latest, v) => (v.versionNumber > latest.versionNumber ? v : latest));
  })();

  const selectedVersion = (() => {
    if (selectedVersionId) {
      const found = versions.find(v => v.id === selectedVersionId);
      if (found) return found;
    }
    return defaultVersion;
  })();

  const isViewingPrimary = selectedVersion?.id === primaryVersion?.id;

  useEffect(() => {
    if (diffSuggestions.length > 0 && actionBarRef.current) {
      const rect = actionBarRef.current.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      const percent = (centerY / window.innerHeight) * 100;
      setSiestaBusyGlowTop(percent);
    }
  }, [diffSuggestions.length, setSiestaBusyGlowTop]);

  const getDocumentContent = () =>
    editorHelpersRef.current?.getContent() ?? selectedVersion?.markdownContent ?? '';

  const contentMetadata = selectedVersion?.markdownContent
    ? { documentContent: selectedVersion.markdownContent }
    : undefined;

  const handleChatStateChange = (state: ChatState, data?: FinalChunkData) => {
    if (state === ChatState.EDITING) {
      setEditState(prev => {
        if (prev.state === EditState.DIFF_REVIEW) return prev;
        const currentContent = editorHelpersRef.current?.getRawContent();
        return {
          ...prev,
          state: EditState.EDITING,
          originalContent: currentContent,
        };
      });
    } else if (state === ChatState.FINAL) {
      if (data && data.editedContent != null) {
        const currentContent = editorHelpersRef.current?.getContent();
        const contentChanged = data.editedContent !== currentContent;
        if (contentChanged) {
          if (data.editedContentWithMarkers) {
            const normalized = markOrphanParents(
              normalizeInlineSuggestions(data.editedContentWithMarkers),
            );
            editorHelpersRef.current?.setContent(normalized, () => {
              requestAnimationFrame(() => {
                const firstInline = Object.values(suggestionsRef.current.inline || {})[0];
                const firstDeletion = Object.values(suggestionsRef.current.deletion || {})[0];
                const firstSuggestion = firstInline ?? firstDeletion;
                firstSuggestion?.scrollIntoView();
              });
            });
            editorHelpersRef.current?.setEditable(false);
            setEditState(prev => ({
              state: EditState.DIFF_REVIEW,
              editedContent: data.editedContent,
              originalContent: prev.originalContent,
              assistantMessage: data.assistantMessage,
              versionId: data.versionId,
              versionNumber: data.versionNumber,
            }));
          } else {
            setEditState(prev => ({
              state: EditState.PREVIEW,
              editedContent: data.editedContent,
              originalContent: prev.originalContent,
              assistantMessage: data.assistantMessage,
              versionId: data.versionId,
              versionNumber: data.versionNumber,
            }));
            editorHelpersRef.current?.setContent(data.editedContent);
          }
        } else {
          setEditState({ state: EditState.IDLE });
        }
      } else {
        setEditState({ state: EditState.IDLE });
      }
    } else if (state === ChatState.ANSWERING || state === ChatState.THINKING) {
      setEditState(prev => {
        if (
          prev.state === EditState.EDITING ||
          prev.state === EditState.PREVIEW ||
          prev.state === EditState.DIFF_REVIEW
        ) {
          return prev;
        }
        return { state: EditState.IDLE };
      });
    }
  };

  useContentMeta({
    pageType: config.pageType,
    entityId: id,
    metadata: contentMetadata,
    chatMode: ChatMode.DOCUMENT,
    onChatStateChange: handleChatStateChange,
    getDocumentContent,
    chatActionsRef,
  });

  const cancelAndRefetch = async () => {
    await queryClient.cancelQueries({ queryKey: ['documents', id, 'versions'] });
    return refetch();
  };

  const handleApplyPreview = async () => {
    if (editState.versionId) {
      const wasPrimary = selectedVersion?.isPrimary;
      if (wasPrimary) {
        await setPrimaryVersion(editState.versionId);
      }
      await cancelAndRefetch();
      setSelectedVersionId(editState.versionId);
      setEditorInstanceKey(editState.versionId);
    } else {
      await cancelAndRefetch();
    }

    setEditState({ state: EditState.IDLE });
  };

  const handleDiscardPreview = async () => {
    try {
      if (editState.versionId) {
        await discardPreviewVersions(
          editState.versionId,
          conversationId,
          deleteDocumentVersions,
          recordConversationDiscard,
          refetch,
        );
      }
    } catch (error) {
      notifyAndLogError('Failed to discard preview', 'Please try again.', error, {
        documentId: id,
        versionId: editState.versionId,
      });
    }
    if (editState.originalContent) {
      editorHelpersRef.current?.setContent(editState.originalContent);
    }
    setEditState({ state: EditState.IDLE });
  };

  const handleEditorReady = (helpers: EditorHelpers) => {
    editorHelpersRef.current = helpers;
    lastSavedContentRef.current = helpers.getContent();
    setHasChanges(false);
  };

  const handleDirty = () => {
    setHasChanges(true);
  };

  const handleSelectVersion = async (versionId: string) => {
    try {
      await revertToVersion(versionId);
      setSelectedVersionId(versionId);
      setEditorInstanceKey(versionId);
    } catch (error) {
      notifyAndLogError('Failed to select version', 'Please try again.', error, {
        documentId: id,
        versionId,
      });
    }
  };

  const handleMakePrimary = (versionId: string) => {
    setPrimaryVersion(versionId);
    setSelectedVersionId(versionId);
    setEditorInstanceKey(versionId);
  };

  const handleSaveVersion = async () => {
    try {
      const content = editorHelpersRef.current?.getContent();
      if (!content) return;
      await createDocumentVersion({ markdownContent: content });
      await refetch();
    } catch (error) {
      notifyAndLogError('Failed to save version', 'Please try again.', error, {
        documentId: id,
      });
    }
  };

  const handleRemoveVersion = async (versionId: string) => {
    try {
      await deleteDocumentVersions([versionId]);
      await refetch();
    } catch (error) {
      notifyAndLogError('Failed to remove version', 'Please try again.', error, {
        documentId: id,
        versionId,
      });
    }
  };

  const handleOverwriteVersion = async (versionId: string) => {
    try {
      const content = editorHelpersRef.current?.getContent();
      if (!content) return;
      await deleteDocumentVersions([versionId]);
      await createDocumentVersion({ markdownContent: content });
      await cancelAndRefetch();
    } catch (error) {
      notifyAndLogError('Failed to overwrite version', 'Please try again.', error, {
        documentId: id,
        versionId,
      });
    }
  };

  const handleSave = async () => {
    if (!selectedVersion) return;

    const content = editorHelpersRef.current?.getContent();
    if (!content) return;

    if (content === lastSavedContentRef.current) {
      setHasChanges(false);
      return;
    }

    setIsSaving(true);
    try {
      const rawContent = editorHelpersRef.current?.getRawContent();
      const markdownContentWithMarkers = rawContent !== content ? rawContent : undefined;
      const result = await createDocumentVersion({
        markdownContent: content,
        markdownContentWithMarkers,
      });
      const newVersionId = result.data.id;
      if (selectedVersion.isPrimary) {
        await setPrimaryVersion(newVersionId);
      }
      await cancelAndRefetch();
      lastSavedContentRef.current = content;
      setHasChanges(false);
      setSelectedVersionId(newVersionId);
      setEditorInstanceKey(newVersionId);
    } catch (error) {
      notifyAndLogError('Failed to save version', 'Please try again.', error, {
        documentId: id,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const backHref = `/client/${clientId}/document/${config.documentTypePath}`;

  const handleFinalise = async () => {
    if (!primaryVersion) return;
    try {
      await saveDocument({ versionId: primaryVersion.id, setAsPrimary: true });
      const content = editorHelpersRef.current?.getContent() ?? primaryVersion.markdownContent;
      if (content && clientId && id) {
        prefetchSuggestedActions(queryClient, id, clientId, content);
      }
      if (
        flagEnabledMeetingNoteClaims &&
        config.documentTypePath === 'meeting-notes' &&
        clientId &&
        id
      ) {
        substantiate(id).catch(() => {});
      }
      navigate(backHref);
    } catch (error) {
      notifyAndLogError('Failed to finalise document', 'Please try again.', error, {
        documentId: id,
        versionId: primaryVersion.id,
      });
    }
  };

  const handleDownloadPdf = async (fileId: number) => {
    try {
      const link = await createLink(fileId);
      window.open(link.url, '_blank');
    } catch (error) {
      notifyAndLogError(
        'Failed to download PDF',
        'An error occurred while trying to download the PDF. Please try again later.',
        error,
        { documentId: id, fileId },
      );
    }
  };

  const handleRemoveDocument = async () => {
    if (!clientId || !id) return;
    try {
      await deleteDocument(clientId, id);
      navigate(backHref);
    } catch (e) {
      notifyAndLogError(
        `Failed to delete ${config.documentLabel}`,
        `An error occurred while trying to delete the ${config.documentLabel}. Please try again later.`,
        e,
        { clientId, documentId: id },
      );
    }
  };

  const handleApplyDiffReview = async () => {
    editorHelpersRef.current?.applyInlineSuggestions();
    editorHelpersRef.current?.setEditable(true);

    const content = editorHelpersRef.current?.getContent();

    let taggedContent: string | undefined;
    if (content) {
      try {
        const cleanContent = content
          .replace(/<suggestion source="deletion">([\s\S]*?)<\/suggestion>/g, '')
          .replace(/<suggestion source="inline">([\s\S]*?)<\/suggestion>/g, '$1');
        const tagged = await tagCitations(cleanContent);
        if (tagged !== cleanContent) {
          taggedContent = tagged;
        }
      } catch {
        Logger.warn('Failed to tag citations after diff review', { documentId: id });
      }
    }

    if (editState.versionId) {
      const wasPrimary = selectedVersion?.isPrimary;
      if (wasPrimary) {
        await setPrimaryVersion(editState.versionId);
      }
      await cancelAndRefetch();
      setEditorInstanceKey(current => current ?? selectedVersion?.id ?? null);
      setSelectedVersionId(editState.versionId);
    } else {
      await cancelAndRefetch();
    }

    if (taggedContent) {
      await new Promise(resolve => requestAnimationFrame(resolve));
      editorHelpersRef.current?.setContent(taggedContent);
    }

    setEditState({ state: EditState.IDLE });
  };

  const handleDiscardDiffReview = async () => {
    try {
      if (editState.versionId) {
        await discardPreviewVersions(
          editState.versionId,
          conversationId,
          deleteDocumentVersions,
          recordConversationDiscard,
          refetch,
        );
      }
    } catch (error) {
      notifyAndLogError('Failed to discard changes', 'Please try again.', error, {
        documentId: id,
        versionId: editState.versionId,
      });
    }
    if (editState.originalContent) {
      editorHelpersRef.current?.setContent(editState.originalContent);
    } else {
      editorHelpersRef.current?.discardSuggestions();
    }
    editorHelpersRef.current?.setEditable(true);
    setEditState({ state: EditState.IDLE });
    chatActionsRef.current?.replaceLastAssistantMessage(
      'Changes discarded. The document was not changed. How else can I help?',
    );
  };

  const handleInlineEditStart = (top: number) => {
    const content = editorHelpersRef.current?.getRawContent();
    if (content) {
      preInlineEditContentRef.current = content;
    }
    setSiestaBusyGlowTop(top);
    previousSiestaModeRef.current = siestaMode === 'busy' ? 'action' : siestaMode;
    setSiestaMode('busy');
  };

  const handleApplyInlineChanges = async () => {
    editorHelpersRef.current?.applyInlineSuggestions();
    await new Promise(resolve => requestAnimationFrame(resolve));
    preInlineEditContentRef.current = null;
    setSiestaBusyGlowTop(null);
    setSiestaMode(previousSiestaModeRef.current ?? 'action');

    const content = editorHelpersRef.current?.getContent();
    if (content) {
      try {
        const cleanContent = content
          .replace(/<suggestion source="deletion">([\s\S]*?)<\/suggestion>/g, '')
          .replace(/<suggestion source="inline">([\s\S]*?)<\/suggestion>/g, '$1');
        const tagged = await tagCitations(cleanContent);
        if (tagged !== cleanContent) {
          editorHelpersRef.current?.setContent(tagged);
        }
      } catch {
        Logger.warn('Failed to tag citations after inline edit', { documentId: id });
      }
    }
  };

  const handleDiscardInlineChanges = () => {
    if (preInlineEditContentRef.current) {
      editorHelpersRef.current?.setContent(preInlineEditContentRef.current);
    } else {
      editorHelpersRef.current?.discardSuggestions();
    }
    preInlineEditContentRef.current = null;
    setSiestaBusyGlowTop(null);
    setSiestaMode(previousSiestaModeRef.current ?? 'action');
    chatActionsRef.current?.replaceLastAssistantMessage(
      'Changes discarded. The document was not changed. How else can I help?',
    );
  };

  const handleInlineEditComplete = () => {
    requestAnimationFrame(() => {
      const currentInlineSuggestions = Object.values(suggestionsRef.current.inline || {});
      const currentDeletionSuggestions = Object.values(suggestionsRef.current.deletion || {});
      if (
        currentInlineSuggestions.length === 0 &&
        currentDeletionSuggestions.length === 0 &&
        preInlineEditContentRef.current !== null
      ) {
        preInlineEditContentRef.current = null;
        setSiestaBusyGlowTop(null);
        setSiestaMode(previousSiestaModeRef.current ?? 'action');
      } else {
        const firstSuggestion = currentInlineSuggestions[0] ?? currentDeletionSuggestions[0];
        firstSuggestion?.scrollIntoView();
      }
    });
  };

  if (isLoadingVersions) {
    return (
      <Center h="100%">
        <Loader />
      </Center>
    );
  }

  return (
    <Flex direction="column" h="100%" pos="relative">
      <ContentPaneOverlay>
        <EditingOverlay mounted={editState.state === EditState.EDITING} />
        <EditPreviewSheet
          opened={editState.state === EditState.PREVIEW}
          editedContent={editState.editedContent ?? ''}
          onApply={handleApplyPreview}
          onDiscard={handleDiscardPreview}
        />
      </ContentPaneOverlay>

      <DocumentPanel
        backHref={backHref}
        title={config.title}
        version={selectedVersion ? `V${selectedVersion.versionNumber}` : ''}
        versionDate={selectedVersion ? formatVersionDate(selectedVersion.createdAt) : ''}
        versionVariant={isViewingPrimary ? 'primary' : 'past'}
        versions={versions}
        selectedVersionId={selectedVersion?.id}
        suggestions={suggestions}
        showMarkers={effectiveShowMarkers}
        showCitations={showCitations}
        showToggle={!hasDiffSuggestions}
        isReviewingDiff={hasDiffSuggestions}
        isSaving={isSaving}
        hasChanges={hasChanges}
        onShowMarkersChange={setShowMarkers}
        onShowCitationsChange={setShowCitations}
        onSave={handleSave}
        onSaveVersion={handleSaveVersion}
        onRemoveVersion={handleRemoveVersion}
        onOverwriteVersion={handleOverwriteVersion}
        onSelectVersion={handleSelectVersion}
        onMakePrimary={handleMakePrimary}
        onDownloadPdf={handleDownloadPdf}
        onFinalise={handleFinalise}
        onRemoveDocument={handleRemoveDocument}
        removeDocumentError={deleteDocumentError}
        onResetRemoveDocumentError={resetDeleteDocument}
        isRemovingDocument={isRemovingDocument}
      />

      <Box mt="md" style={{ flex: 1, overflow: 'auto' }}>
        {(selectedVersion?.markdownContentWithMarkers ?? selectedVersion?.markdownContent) && (
          <MarkdownEditor
            key={editorInstanceKey ?? selectedVersion.id}
            defaultValue={
              selectedVersion.markdownContentWithMarkers ?? selectedVersion.markdownContent
            }
            bare
            onDirty={handleDirty}
            onSuggestionsChange={handleSuggestionsChange}
            onEditorReady={handleEditorReady}
            showMarkers
            hiddenSources={hiddenSources}
            enableInlineEditing
            documentId={id}
            clientId={clientId}
            conversationId={conversationId}
            onInlineEditStart={handleInlineEditStart}
            onInlineEditComplete={handleInlineEditComplete}
            hasInlineEdits={diffSuggestions.length > 0}
          />
        )}
      </Box>

      {diffSuggestions.length > 0 && (
        <Box ref={actionBarRef} pos="absolute" bottom={40} left={32} right={32}>
          <SiestaActionBar
            suggestions={diffSuggestions}
            onApply={
              editState.state === EditState.DIFF_REVIEW
                ? handleApplyDiffReview
                : handleApplyInlineChanges
            }
            onDiscard={
              editState.state === EditState.DIFF_REVIEW
                ? handleDiscardDiffReview
                : handleDiscardInlineChanges
            }
          />
        </Box>
      )}
    </Flex>
  );
}

async function discardPreviewVersions(
  versionId: string,
  conversationId: string | undefined,
  deleteDocumentVersions: (ids: string[]) => Promise<unknown>,
  recordConversationDiscard: (id: string) => Promise<unknown>,
  refetch: () => Promise<unknown>,
) {
  const results = await Promise.allSettled([
    deleteDocumentVersions([versionId]),
    conversationId ? recordConversationDiscard(conversationId) : Promise.resolve(),
  ]);

  await refetch();

  const failed = results.filter(r => r.status === 'rejected');
  if (failed.length > 0) {
    throw (failed[0] as PromiseRejectedResult).reason;
  }
}
